import { useState, useRef, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { useAddressSuggestions } from "@/hooks/useAddressSuggestions";
import {
  normalizeAddressInput,
  parseAddressInput,
  validateCityInput,
} from "@/lib/addressInput";

interface AddressSuggestion {
  display: string;
  city: string;
  region: string;
  address?: string;
  postalCode?: string;
  lat: number;
  lng: number;
  // Internal fields for precise geocoding
  _needsPreciseGeocode?: boolean;
  _streetName?: string;
  _houseNumber?: string;
}

interface AddressAutocompleteProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onSuggestionSelect: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  required?: boolean;
  type: "city" | "address";
  disabled?: boolean;
  filterByCity?: string; // Miasto do filtrowania wyników (dla adresów)
}

// Helper function to fetch postal code via reverse geocoding
const fetchPostalCodeForCoords = async (lat: number, lng: number): Promise<string | null> => {
  try {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lon: lng.toString(),
      format: "json",
      addressdetails: "1",
      zoom: "18", // High zoom = more precise address data
    });
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?${params}`,
      {
        headers: {
          "User-Agent": "GdzieSchronisko-ShelterApp/1.0",
          "Accept-Language": "pl",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      return data?.address?.postcode || null;
    }
  } catch {
    // Ignore errors
  }
  return null;
};

// Helper function to geocode a full address (street + house number) for precise coordinates
const geocodeFullAddress = async (
  street: string,
  houseNumber: string,
  city: string
): Promise<{ lat: number; lng: number; postcode?: string } | null> => {
  try {
    const fullAddress = `${street} ${houseNumber}, ${city}, Polska`;
    const params = new URLSearchParams({
      q: fullAddress,
      format: "json",
      limit: "1",
      countrycodes: "pl",
      addressdetails: "1",
    });
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params}`,
      {
        headers: {
          "User-Agent": "GdzieSchronisko-ShelterApp/1.0",
          "Accept-Language": "pl",
        },
      }
    );
    if (response.ok) {
      const data = await response.json();
      if (data && data.length > 0) {
        const result = data[0];
        const lat = parseFloat(result.lat);
        const lng = parseFloat(result.lon);
        // Validate coordinates are in Poland
        if (lat >= 49 && lat <= 55 && lng >= 14 && lng <= 24) {
          return {
            lat,
            lng,
            postcode: result.address?.postcode || undefined,
          };
        }
      }
    }
  } catch {
    // Ignore errors, fall back to original coordinates
  }
  return null;
};

import { AUTOCOMPLETE_COPY } from "@/lib/addressAutocompleteCopy";
import { RETRY } from "@/lib/uiMessages";

export const AddressAutocomplete = ({
  id,
  value,
  onChange,
  onSuggestionSelect,
  placeholder,
  required,
  type,
  disabled,
  filterByCity,
}: AddressAutocompleteProps) => {
  const copy = AUTOCOMPLETE_COPY[type];
  const effectivePlaceholder = placeholder ?? copy.placeholder;
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedFromList, setSelectedFromList] = useState(false);
  const [userIsTyping, setUserIsTyping] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { suggestions, isLoading, error, fetchSuggestions, clearSuggestions } = useAddressSuggestions();

  // Stabilny "ostatni stan końcowy" - nie znika podczas kolejnych wpisywań.
  // Uaktualniamy go dopiero, gdy hook skończy ładować nowe wyniki.
  const [stableView, setStableView] = useState<{
    items: AddressSuggestion[];
    error: string | null;
    hadResponse: boolean;
  }>({ items: [], error: null, hadResponse: false });

  useEffect(() => {
    if (!isLoading) {
      setStableView({ items: suggestions, error, hadResponse: true });
    }
  }, [isLoading, suggestions, error]);

  // Walidacja + normalizacja inputu - mniej empty state przy literówkach.
  const inputAnalysis = useMemo(() => {
    if (type === "city") {
      const v = validateCityInput(value);
      return {
        searchTerm: value.trim(),
        warning: v.warning,
        parserHint: null as string | null,
      };
    }
    const parsed = parseAddressInput(value);
    return {
      searchTerm: parsed.searchTerm || normalizeAddressInput(value),
      warning: null as string | null,
      parserHint: parsed.hint,
    };
  }, [value, type]);

  // Minimalna liczba znaków: dla adresów 3 (mniej szumu/zapytań), dla miast 2.
  const minQueryLength = type === "address" ? 3 : 2;

  // Pobierz sugestie przy zmianie wartości
  useEffect(() => {
    const term = inputAnalysis.searchTerm;
    if (term.length >= minQueryLength && userIsTyping) {
      // Dla adresów wysyłamy znormalizowany pełny tekst (z numerem domu),
      // żeby hook mógł sam wyciągnąć number; dla miast - sam termin.
      const queryToSend =
        type === "address" ? normalizeAddressInput(value) : term;
      fetchSuggestions(queryToSend, type, filterByCity);
      setShowSuggestions(true);
      setSelectedFromList(false);
    } else if (term.length < minQueryLength) {
      clearSuggestions();
      setShowSuggestions(false);
    }
    setUserIsTyping(false);
  }, [
    value,
    type,
    filterByCity,
    fetchSuggestions,
    clearSuggestions,
    userIsTyping,
    inputAnalysis.searchTerm,
    minQueryLength,
  ]);

  // Zamknij sugestie po kliknięciu poza komponentem
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserIsTyping(true);
    onChange(e.target.value);
    setHighlightedIndex(-1);
  };

  const handleSuggestionClick = async (suggestion: AddressSuggestion) => {
    setSelectedFromList(true);
    setShowSuggestions(false);
    clearSuggestions();
    
    let finalSuggestion = { ...suggestion };
    
    // If suggestion needs precise geocoding (has house number), do it now
    if (suggestion._needsPreciseGeocode && suggestion._streetName && suggestion._houseNumber && suggestion.city) {
      const preciseCoords = await geocodeFullAddress(
        suggestion._streetName,
        suggestion._houseNumber,
        suggestion.city
      );
      if (preciseCoords) {
        finalSuggestion = {
          ...finalSuggestion,
          lat: preciseCoords.lat,
          lng: preciseCoords.lng,
          postalCode: preciseCoords.postcode || finalSuggestion.postalCode,
        };
      }
    }
    
    // If still no postal code, try to fetch it via reverse geocode
    if (!finalSuggestion.postalCode && finalSuggestion.lat && finalSuggestion.lng) {
      const postalCode = await fetchPostalCodeForCoords(finalSuggestion.lat, finalSuggestion.lng);
      if (postalCode) {
        finalSuggestion = { ...finalSuggestion, postalCode };
      }
    }
    
    // Clean up internal fields before passing to parent
    const { _needsPreciseGeocode, _streetName, _houseNumber, ...cleanSuggestion } = finalSuggestion;
    onSuggestionSelect(cleanSuggestion);
  };

  // Auto-select first suggestion on blur if user didn't select from list
  const handleBlur = () => {
    setTimeout(async () => {
      if (!selectedFromList && userIsTyping === false && suggestions.length > 0 && value.length >= minQueryLength && showSuggestions) {
        // Auto-select the first matching suggestion
        const firstSuggestion = suggestions[0];
        if (firstSuggestion) {
          let suggestionToUse = { ...firstSuggestion };
          
          // If suggestion needs precise geocoding (has house number), do it now
          if (firstSuggestion._needsPreciseGeocode && firstSuggestion._streetName && firstSuggestion._houseNumber && firstSuggestion.city) {
            const preciseCoords = await geocodeFullAddress(
              firstSuggestion._streetName,
              firstSuggestion._houseNumber,
              firstSuggestion.city
            );
            if (preciseCoords) {
              suggestionToUse = {
                ...suggestionToUse,
                lat: preciseCoords.lat,
                lng: preciseCoords.lng,
                postalCode: preciseCoords.postcode || suggestionToUse.postalCode,
              };
            }
          }
          
          // If still no postal code, try to fetch it
          if (!suggestionToUse.postalCode && suggestionToUse.lat && suggestionToUse.lng) {
            const postalCode = await fetchPostalCodeForCoords(suggestionToUse.lat, suggestionToUse.lng);
            if (postalCode) {
              suggestionToUse = { ...suggestionToUse, postalCode };
            }
          }
          
          // Clean up internal fields before passing to parent
          const { _needsPreciseGeocode, _streetName, _houseNumber, ...cleanSuggestion } = suggestionToUse;
          onSuggestionSelect(cleanSuggestion);
          setSelectedFromList(true);
        }
      }
      setShowSuggestions(false);
    }, 200);
  };

  const items = stableView.items;
  const hasError = !!stableView.error;
  const isFirstLoading = isLoading && !stableView.hadResponse;
  const listboxId = `${id}-listbox`;

  const triggerRetry = () => {
    if (value.length >= minQueryLength) {
      fetchSuggestions(value, type, filterByCity);
      setShowSuggestions(true);
    }
  };

  // Reset highlight gdy lista się zmienia (zapobiega wskazywaniu nieistniejącego indeksu).
  useEffect(() => {
    setHighlightedIndex((prev) => (prev >= items.length ? -1 : prev));
  }, [items]);

  // Scroll do wyróżnionego elementu - klawisze nie powinny "uciekać" za widok.
  useEffect(() => {
    if (highlightedIndex < 0) return;
    const el = document.getElementById(`${id}-option-${highlightedIndex}`);
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, id]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (showSuggestions) {
        e.preventDefault();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
      return;
    }

    if (!showSuggestions && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      if (value.length >= minQueryLength) {
        e.preventDefault();
        setShowSuggestions(true);
        return;
      }
    }

    if (!showSuggestions) return;

    if (e.key === "Enter" && hasError) {
      e.preventDefault();
      triggerRetry();
      return;
    }

    if (items.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev <= 0 ? items.length - 1 : prev - 1));
        break;
      case "Home":
        e.preventDefault();
        setHighlightedIndex(0);
        break;
      case "End":
        e.preventDefault();
        setHighlightedIndex(items.length - 1);
        break;
      case "PageDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          Math.min(items.length - 1, (prev < 0 ? 0 : prev) + 4)
        );
        break;
      case "PageUp":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(0, (prev < 0 ? 0 : prev) - 4));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < items.length) {
          handleSuggestionClick(items[highlightedIndex]);
        } else {
          handleSuggestionClick(items[0]);
        }
        break;
      case "Tab":
        if (highlightedIndex >= 0 && highlightedIndex < items.length) {
          handleSuggestionClick(items[highlightedIndex]);
        }
        setShowSuggestions(false);
        break;
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => {
            if (items.length > 0 || hasError) {
              setShowSuggestions(true);
            }
          }}
          placeholder={effectivePlaceholder}
          aria-describedby={`${id}-hint`}
          required={required}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showSuggestions && value.length >= minQueryLength}
          aria-controls={listboxId}
          aria-activedescendant={
            showSuggestions && highlightedIndex >= 0
              ? `${id}-option-${highlightedIndex}`
              : undefined
          }
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {(inputAnalysis.warning ?? inputAnalysis.parserHint ?? copy.hint) && (
        <p
          id={`${id}-hint`}
          className={`mt-1 text-xs ${
            inputAnalysis.warning ? "text-destructive" : "text-muted-foreground"
          }`}
        >
          {inputAnalysis.warning ?? inputAnalysis.parserHint ?? copy.hint}
        </p>
      )}

      {showSuggestions && value.length >= minQueryLength && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {isFirstLoading ? (
            <div className="px-3 py-3 text-sm text-muted-foreground flex items-center gap-2" role="status">
              <Loader2 className="w-4 h-4 animate-spin" />
              {copy.loading}
            </div>
          ) : hasError ? (
            <div className="px-3 py-3 text-sm flex items-start justify-between gap-2" role="alert">
              <span className="text-destructive">{stableView.error}</span>
              <button
                type="button"
                className="shrink-0 text-xs font-medium text-primary hover:underline"
                // onMouseDown zamiast onClick - input traci focus na blur i zamyka dropdown przed click.
                onMouseDown={(e) => {
                  e.preventDefault();
                  triggerRetry();
                }}
              >
                {RETRY}
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="px-3 py-3 text-sm text-muted-foreground" role="status">
              {copy.empty}
            </div>
          ) : (
            items.map((suggestion, index) => (
              <button
                key={`${suggestion.display}-${index}`}
                id={`${id}-option-${index}`}
                type="button"
                role="option"
                aria-selected={index === highlightedIndex}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors ${
                  index === highlightedIndex ? "bg-accent text-accent-foreground" : ""
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {suggestion.display}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
